import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {useUpdateEffect} from 'ahooks';
import {EdgeEffectsOptions} from '@Panels/poster-editor-item-effects-panel/components/edge-effects-options';
import {GlowMobileColorPanel} from '@Panels/poster-editor-item-effects-panel/components/glow-mobile-color-panel';
import {ShadowMobileColorPanel} from '@Panels/poster-editor-item-shadow-panel/components/shadow-mobile-color-panel';
import {RemoveColorOptions} from '@Panels/poster-editor-item-effects-panel/components/remove-color-options';
import {RemovecolorMobilePanel} from '@Panels/poster-editor-item-effects-panel/components/removecolor-mobile-panel';
import {BorderEffectOptions} from '@Panels/poster-editor-item-effects-panel/components/border-effect-options';
import {BorderMobileColorPanel} from '@Panels/poster-editor-item-effects-panel/components/border-mobile-color-panel';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {PosterEditorItemEffects, POSTER_EDITOR_ITEM_EFFECTS_DATA_ARRAY} from '@Panels/poster-editor-item-effects-panel/poster-editor-item-effects-panel.types';
import {getAssetUrl} from '@Utils/s3.util';
import {ContextualMenu} from '@Components/contextual-menu';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import {ContextualMenuHorizontalScrollingSection} from '@Components/contextual-menu/components/contextual-menu-horizontal-scrolling-section';
import styles from './poster-editor-item-effects-panel.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {ItemMobileShadowOptions} from '@Panels/poster-editor-item-shadow-panel/components/item-mobile-shadow-options/item-mobile-shadow-options';
import {ItemMobileGlowEffectOptions} from '@Panels/poster-editor-item-effects-panel/components/item-mobile-glow-effect-options/item-mobile-glow-effect-options';

interface PosterEditorItemEffectsPanelProps {
  panelId: string;
}

enum PanelToShow {
  COLOR_PANEL = 'COLOR_PANEL',
  DEFAULT = 'DEFAULT',
}

const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/effects/';

export default function PosterEditorItemEffectsPanel({...props}: PosterEditorItemEffectsPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const [panelToShow, setPanelToShow] = useState(PanelToShow.DEFAULT);
  const [currentlySelectedPanel, setCurrentlySelectedPanel] = useState<PosterEditorItemEffects | null>(null);
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  useUpdateEffect((): void => {
    if (activeItem?.isSlideshow()) {
      onClose();
    }
  }, [activeSlideUID]);

  useEffect((): void => {
    if (!currentlySelectedPanel) {
      updateHeightInRedux('182px');
    }
  }, [currentlySelectedPanel]);

  const updateHeightInRedux = (height: string): void => {
    updateOptionsPanelHeight(props.panelId, height);
  };

  const getHeaderText = (): string => {
    switch (currentlySelectedPanel) {
      case PosterEditorItemEffects.BORDER:
        return window.i18next.t('pmwjs_border');
      case PosterEditorItemEffects.EDGE_EFFECTS:
        return window.i18next.t('pmwjs_edge_effects');
      case PosterEditorItemEffects.REMOVE_COLOR:
        return window.i18next.t('pmwjs_remove_color');
      case PosterEditorItemEffects.GLOW:
        return window.i18next.t('pmwjs_glow');
      case PosterEditorItemEffects.SHADOW:
        return window.i18next.t('pmwjs_shadow');
      default:
        return window.i18next.t('pmwjs_effects');
    }
  };

  const showDefaultPanelBackButton = (): boolean => {
    return !!currentlySelectedPanel;
  };

  const onBackButton = (): void => {
    setCurrentlySelectedPanel(null);
  };

  const getHeader = (): ReactElement => {
    return <PanelHeader showBackButton={showDefaultPanelBackButton()} onBackButton={onBackButton} onClose={onClose} title={getHeaderText()} />;
  };

  const getTextFromType = (effect: PosterEditorItemEffects): string => {
    switch (effect) {
      case PosterEditorItemEffects.BORDER:
        return window.i18next.t('pmwjs_border');
      case PosterEditorItemEffects.EDGE_EFFECTS:
        return window.i18next.t('pmwjs_edge_effects');
      case PosterEditorItemEffects.GLOW:
        return window.i18next.t('pmwjs_glow');
      case PosterEditorItemEffects.REMOVE_COLOR:
        return window.i18next.t('pmwjs_remove_color');
      case PosterEditorItemEffects.SHADOW:
        return window.i18next.t('pmwjs_shadow');
      default:
        throw new Error('unhandled effect');
    }
  };

  const isIcon = (): boolean => {
    if (activeItem?.isImage()) {
      return activeItem.isIcon();
    }
    return false;
  };

  const doesEffectExistForIcon = (type: PosterEditorItemEffects): boolean => {
    return type !== PosterEditorItemEffects.EDGE_EFFECTS;
  };

  const getThumbnailContainerClasses = (effect: PosterEditorItemEffects): string => {
    switch (effect) {
      case PosterEditorItemEffects.GLOW:
      case PosterEditorItemEffects.EDGE_EFFECTS:
        return '';
      case PosterEditorItemEffects.SHADOW:
      case PosterEditorItemEffects.REMOVE_COLOR:
      case PosterEditorItemEffects.BORDER:
      default:
        return styles.thumbnail;
    }
  };

  const getItems = (): (ReactElement | null)[] => {
    return POSTER_EDITOR_ITEM_EFFECTS_DATA_ARRAY.map((effectData) => {
      if (activeItem?.isImage() && activeItem.isDrawing() && effectData.effect === PosterEditorItemEffects.EDGE_EFFECTS) {
        return null;
      }
      if (activeItem?.isVideo() && effectData.effect === PosterEditorItemEffects.REMOVE_COLOR) {
        return null;
      }
      if (activeItem?.isSlideshow() && activeSlide?.isVideoSlide() && effectData.effect === PosterEditorItemEffects.REMOVE_COLOR) {
        return null;
      }
      if (activeItem?.isSticker() && effectData.effect === PosterEditorItemEffects.REMOVE_COLOR) {
        return null;
      }
      if (activeItem?.isSlideshow() && effectData.effect === PosterEditorItemEffects.BORDER) {
        return null;
      }
      if (isIcon() && !doesEffectExistForIcon(effectData.effect)) {
        return null;
      }
      return (
        <ContextualMenuThumbnailItem
          key={effectData.effect}
          className={styles.items}
          thumbnailContainerClassName={styles.largeThumbnail}
          thumbnail={
            <Thumbnail
              className={getThumbnailContainerClasses(effectData.effect)}
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}${effectData.thumbnail}.${getCompatibleImageFileExtension('webp')}`),
              }}
            />
          }
          hasBorder={false}
          selectionState={ThumbnailItemSelectionState.OUTLINE}
          text={getTextFromType(effectData.effect)}
          onClick={(): void => {
            setCurrentlySelectedPanel(effectData.effect);
          }}
        />
      );
    });
  };

  const getDefaultContent = (): ReactElement => {
    return (
      <ContextualMenu id="item-effects-panel" hasMobileMorphingProperties includeLeftRightPadding={false} contentClassName="spacing-m-0 spacing-p-t-1 spacing-p-b-4">
        <ContextualMenuHorizontalScrollingSection className={`flex-h-row spacing-p-r-4 spacing-p-l-4 spacing-m-0 ${styles.list}`}>
          {getItems()}
        </ContextualMenuHorizontalScrollingSection>
      </ContextualMenu>
    );
  };

  const showColorPanel = (): void => {
    setPanelToShow(PanelToShow.COLOR_PANEL);
  };

  const hideColorPanel = (): void => {
    setPanelToShow(PanelToShow.DEFAULT);
  };

  const getPanelContent = (): ReactElement => {
    switch (currentlySelectedPanel) {
      case PosterEditorItemEffects.EDGE_EFFECTS:
        return <EdgeEffectsOptions panelId={props.panelId} />;
      case PosterEditorItemEffects.GLOW:
        return <ItemMobileGlowEffectOptions panelId={props.panelId} itemType={activeItem?.gitype} onClickColorFill={showColorPanel} />;
      case PosterEditorItemEffects.BORDER:
        return <BorderEffectOptions panelId={props.panelId} onClickColorFill={showColorPanel} />;
      case PosterEditorItemEffects.REMOVE_COLOR:
        return <RemoveColorOptions panelId={props.panelId} onClickColorFill={showColorPanel} />;
      case PosterEditorItemEffects.SHADOW:
        return (
          <ContextualMenu id="mobile-shadow-panel" hasMobileMorphingProperties hasMobileModalPadding>
            <ItemMobileShadowOptions panelId={props.panelId} itemType={activeItem?.gitype} onClickColorOption={showColorPanel} />
          </ContextualMenu>
        );
      default:
        return getDefaultContent();
    }
  };

  const getDefaultPanels = (): ReactElement => {
    return <Panel panelHeader={getHeader()} panelContent={getPanelContent()} />;
  };

  const getColorPanels = (): ReactElement | null => {
    switch (currentlySelectedPanel) {
      case PosterEditorItemEffects.SHADOW:
        return <ShadowMobileColorPanel onBack={hideColorPanel} onClose={onClose} panelId={props.panelId} />;
      case PosterEditorItemEffects.GLOW:
        return <GlowMobileColorPanel onBack={hideColorPanel} onClose={onClose} panelId={props.panelId} />;
      case PosterEditorItemEffects.BORDER:
        return <BorderMobileColorPanel onBack={hideColorPanel} onClose={onClose} panelId={props.panelId} />;
      case PosterEditorItemEffects.REMOVE_COLOR:
        return <RemovecolorMobilePanel onBack={hideColorPanel} onClose={onClose} panelId={props.panelId} />;
      case PosterEditorItemEffects.EDGE_EFFECTS:
      default:
        return null;
    }
  };

  const getPanel = (): ReactElement | null => {
    switch (panelToShow) {
      case PanelToShow.COLOR_PANEL:
        return getColorPanels();
      case PanelToShow.DEFAULT:
      default:
        return getDefaultPanels();
    }
  };

  return getPanel();
}
